import type { CodeSessionEvent } from '@/lib/code-sessions/types';

export type MergedEvents = {
  /** The log sorted by seq and deduped; the input log itself when nothing was added. */
  events: CodeSessionEvent[];
  /** Incoming events the log did not hold, in arrival order. */
  added: CodeSessionEvent[];
  /** False when `events` is the previous log with `added` appended at its end. */
  rewritten: boolean;
};

/** `list` extended by `more`, or `list` itself when there is nothing to add. */
export function appendEvents<T extends CodeSessionEvent>(list: T[], more: readonly T[]): T[] {
  return more.length === 0 ? list : [...list, ...more];
}

/**
 * Merge incoming events into a log sorted by seq. Live batches arrive in order
 * after everything already held, so they append in time proportional to the
 * batch; snapshots and recovery pages that overlap or fill gaps re-sort.
 */
export function mergeBySeq(
  log: CodeSessionEvent[],
  incoming: readonly CodeSessionEvent[],
): MergedEvents {
  let previousSeq = log.at(-1)?.seq ?? -Infinity;
  const appends = incoming.every((event) => {
    const ordered = event.seq > previousSeq;
    previousSeq = event.seq;
    return ordered;
  });
  if (appends) {
    return { events: appendEvents(log, incoming), added: [...incoming], rewritten: false };
  }
  const bySeq = new Map(log.map((event) => [event.seq, event]));
  const added: CodeSessionEvent[] = [];
  for (const event of incoming) {
    if (!bySeq.has(event.seq)) {
      bySeq.set(event.seq, event);
      added.push(event);
    }
  }
  return added.length === 0
    ? { events: log, added, rewritten: false }
    : { events: [...bySeq.values()].sort((a, b) => a.seq - b.seq), added, rewritten: true };
}

/** Last seq of the contiguous run that starts right after `cursor` in a sorted log. */
export function advanceCursor(events: readonly CodeSessionEvent[], cursor: number): number {
  // Binary search for the first event after the cursor.
  let low = 0;
  let high = events.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if ((events[middle] as CodeSessionEvent).seq <= cursor) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  let next = cursor;
  for (let index = low; index < events.length && events[index]?.seq === next + 1; index += 1) {
    next += 1;
  }
  return next;
}
